import { useState } from "react";
import type { ReactNode } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { Box, Button, Group, ScrollArea, Table, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconSearch, IconSelector } from "@tabler/icons-react";
import { LoadingState } from "./states";

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  loading?: boolean;
  /** Row click handler (usually navigates to the detail screen). */
  onRowClick?: (row: T) => void;
  /** Show the client-side global text filter input. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** "Load more" footer wiring from usePaginatedQuery. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  emptyState?: ReactNode;
}

export function DataTable<T>({
  data,
  columns,
  loading,
  onRowClick,
  searchable = true,
  searchPlaceholder = "Filter…",
  hasMore,
  onLoadMore,
  emptyState,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const rows = table.getRowModel().rows;

  return (
    <Box>
      {searchable ? (
        <Group mb="sm" justify="space-between">
          <TextInput
            leftSection={<IconSearch size={16} />}
            placeholder={searchPlaceholder}
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.currentTarget.value)}
            w={320}
          />
          <Text size="sm" c="dimmed">
            {rows.length} row{rows.length === 1 ? "" : "s"}
            {hasMore ? "+" : ""}
          </Text>
        </Group>
      ) : null}

      <ScrollArea>
        <Table highlightOnHover stickyHeader verticalSpacing="xs" horizontalSpacing="md" miw={640}>
          <Table.Thead>
            {table.getHeaderGroups().map((hg) => (
              <Table.Tr key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <Table.Th key={header.id} style={{ whiteSpace: "nowrap" }}>
                      {header.isPlaceholder ? null : canSort ? (
                        <UnstyledButton
                          onClick={header.column.getToggleSortingHandler()}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                          <Text size="xs" fw={700} tt="uppercase" c="dimmed" lts={0.4}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Text>
                          {sorted === "asc" ? (
                            <IconChevronUp size={13} />
                          ) : sorted === "desc" ? (
                            <IconChevronDown size={13} />
                          ) : (
                            <IconSelector size={13} style={{ opacity: 0.4 }} />
                          )}
                        </UnstyledButton>
                      ) : (
                        <Text size="xs" fw={700} tt="uppercase" c="dimmed" lts={0.4}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Text>
                      )}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Td key={cell.id} style={{ whiteSpace: "nowrap" }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {loading ? <LoadingState /> : null}
      {!loading && rows.length === 0 ? <Box py="md">{emptyState}</Box> : null}

      {hasMore && onLoadMore ? (
        <Group justify="center" mt="md">
          <Button variant="default" size="xs" onClick={onLoadMore}>
            Load more
          </Button>
        </Group>
      ) : null}
    </Box>
  );
}
